<script setup lang="ts">
import { ref, toRef } from 'vue';
import { AxiosResponse } from 'axios';
import Goal from './Goal.vue';
import { TreatmentGoalFormInterface } from '@/types/TreatmentPlans';
import useUserTreatmentPlanStore from '@/store/People/Person/TreatmentPlans';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface Props {
  goals: TreatmentGoalFormInterface[];
  serviceId: number;
  loading?: boolean;
  parentElement: string;
}

const treatmentPlansStore = useUserTreatmentPlanStore();
const props = defineProps<Props>();
const emit = defineEmits([EMIT.REMOVE, EMIT.NEW_GOAL, EMIT.SUCCESS, EMIT.UPDATE, EMIT.ERROR]);
const goals = toRef(props, 'goals');
const newGoals = ref<TreatmentGoalFormInterface[]>([]);

const addEmptyGoal = () => {
  newGoals.value.push({
    description: '',
    newGoalId: Math.random(),
  });
  setTimeout(() => {
    const serviceScroll = document.getElementById(props.parentElement);
    if (serviceScroll) serviceScroll.scrollTop = serviceScroll.scrollHeight;
  }, 100);
};

const onGoalRemoved = async (editedGoal: TreatmentGoalFormInterface) => {
  /**
   * if there is a goalId then the goal has been saved on the API, if not it was just created
   * and therefore we dont need to delete on the server, so just pop it off the goals array
   */
  await treatmentPlansStore
    .deleteGoal({ treatment_goals: [editedGoal.id] as number[] })
    .then(onRemoveSuccess)
    .catch(onRemoveFailed);
};

const onNewGoalRemove = (newGoal: TreatmentGoalFormInterface) => {
  newGoals.value = newGoals.value.filter((a) => a.newGoalId !== newGoal.newGoalId);
};

const onGoalUpdate = (msg: string) => emit(EMIT.UPDATE, msg);

const onGoalError = (msg: string) => emit(EMIT.ERROR, msg);

const onGoalSuccess = (msg: string) => emit(EMIT.SUCCESS, msg);

const onRemoveSuccess = () => emit(EMIT.REMOVE);

const onRemoveFailed = (error: AxiosResponse) => emit(EMIT.ERROR, error.data.message);

const onNewGoal = (goal: TreatmentGoalFormInterface) => {
  emit(EMIT.NEW_GOAL);
  onNewGoalRemove(goal);
};
</script>

<template>
  <div
    v-for="goal in goals"
    :key="goal.id"
    class="flex flex-column"
    :class="{ 'pr-3': goals.length + newGoals.length < 3 }"
  >
    <Goal
      :goal="goal"
      :service-id="serviceId"
      @remove="onGoalRemoved"
      @new-goal="onNewGoal"
      @update="onGoalUpdate"
      @error="onGoalError"
      @success="onGoalSuccess"
    />
  </div>
  <div
    v-for="goal in newGoals"
    :key="goal.newGoalId"
    class="flex flex-column"
    :class="{ 'pr-3': goals.length + newGoals.length < 3 }"
  >
    <Goal
      :goal="goal"
      :service-id="serviceId"
      @remove="onNewGoalRemove"
      @new-goal="onNewGoal"
      @update="onGoalUpdate"
      @error="onGoalError"
      @success="onGoalSuccess"
    />
  </div>
  <div v-if="!goals.length" class="w-full m-3 mb-0 p-3 pb-0 text-sm text-600">
    <span>No goals have been added to this service</span>
  </div>
  <div class="add-goal-button">
    <AppButton
      v-if="can([PERMISSIONS.TREATMENT_SERVICES_GOALS_STORE])"
      type="secondary"
      icon-left="add"
      text="Add Goal"
      @click="addEmptyGoal"
    />
  </div>
</template>

<style lang="scss">
.add-goal-button {
  position: sticky;
  bottom: 0rem;
  padding: 1rem;
  margin: 1rem 0rem 0rem 0rem;
  background: rgba(white, 0.5);
  backdrop-filter: blur(0.5rem);
}
</style>
